/**
 * Calendar-day ranges for the custom date picker. Days are `YYYY-MM-DD` strings, both ends
 * inclusive; the arithmetic runs in UTC so daylight-saving changes never shift a day. Which
 * calendar the days belong to (the viewer's, or UTC) is the screen's choice: `rangeBounds`
 * turns a range into the instants an API filters on.
 */
const DAY = 86_400_000;
const pad = (n) => String(n).padStart(2, '0');
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DAY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const TIME_RE = /^([01]\d|2[0-3]):([0-5]\d)$/;
/** `YYYY-MM-DD` of a Date, in the given zone. */
export function dayOf(d, zone = 'local') {
    return zone === 'utc' ? `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}` : `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
export const todayIn = (zone = 'local', now = new Date()) => dayOf(now, zone);
/** True for a real calendar day in `YYYY-MM-DD` form. */
export function isDay(s) {
    if (typeof s !== 'string')
        return false;
    const m = DAY_RE.exec(s);
    if (!m)
        return false;
    const d = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3]));
    return d.getUTCFullYear() === +m[1] && d.getUTCMonth() === +m[2] - 1;
}
export const isTime = (s) => typeof s === 'string' && TIME_RE.test(s);
const utcMs = (day) => Date.parse(`${day}T00:00:00Z`);
const fromUtcMs = (ms) => dayOf(new Date(ms), 'utc');
export const addDays = (day, n) => fromUtcMs(utcMs(day) + n * DAY);
/** Same day `n` months away, clamped to the month's last day (31 Mar − 1 month = 28/29 Feb). */
export function addMonths(day, n) {
    const [y, m, d] = day.split('-').map(Number);
    const last = new Date(Date.UTC(y, m - 1 + n + 1, 0)).getUTCDate();
    return fromUtcMs(Date.UTC(y, m - 1 + n, Math.min(d, last)));
}
export const monthStart = (day) => `${day.slice(0, 8)}01`;
export const monthEnd = (day) => addDays(addMonths(monthStart(day), 1), -1);
/** Days in the range, both ends counted. */
export const spanDays = (from, to) => Math.round((utcMs(to) - utcMs(from)) / DAY) + 1;
/** Monday-first weekday, 0–6. */
export const weekday = (day) => (new Date(utcMs(day)).getUTCDay() + 6) % 7;
/** "3 Sep", with the year when it isn't the current one: "3 Sep 2025". */
export function dayLabel(day, today = todayIn()) {
    const [y, m, d] = day.split('-').map(Number);
    return `${d} ${MONTHS[m - 1]}${String(y) === today.slice(0, 4) ? '' : ` ${y}`}`;
}
/** "3 Sep – 17 Oct", "12 Oct" for one day, with times when the range has them. */
export function rangeLabel(r, today = todayIn()) {
    const a = `${dayLabel(r.from, today)}${r.fromTime ? ` ${r.fromTime}` : ''}`;
    const b = `${dayLabel(r.to, today)}${r.toTime ? ` ${r.toTime}` : ''}`;
    return a === b || (r.from === r.to && !r.fromTime && !r.toTime) ? a : `${a} – ${b}`;
}
/** `DD/MM/YYYY` for a typed date field. */
export function dayToTyped(day) {
    return day ? `${day.slice(8, 10)}/${day.slice(5, 7)}/${day.slice(0, 4)}` : '';
}
/** Reads a typed `DD/MM/YYYY` (also `D/M/YYYY`, `.` or `-` separators); null when it isn't a real day. */
export function typedToDay(s) {
    const m = /^\s*(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})\s*$/.exec(s);
    if (!m)
        return null;
    const day = `${m[3]}-${pad(+m[2])}-${pad(+m[1])}`;
    return isDay(day) ? day : null;
}
/** Start of `from` and end of `to` as epoch ms, `[start, end)`. */
export function rangeBounds(r, zone = 'local') {
    const at = (day, time) => (zone === 'utc' ? Date.parse(`${day}T${time}:00Z`) : new Date(`${day}T${time}:00`).getTime());
    const start = at(r.from, r.fromTime ?? '00:00');
    const end = r.toTime ? at(r.to, r.toTime) + 60_000 : at(addDays(r.to, 1), '00:00');
    return [start, end];
}
/** `from=…&to=…` (ISO instants, `to` exclusive) for an API that filters by time. */
export function rangeQuery(r, zone = 'local') {
    const [a, b] = rangeBounds(r, zone);
    return `from=${encodeURIComponent(new Date(a).toISOString())}&to=${encodeURIComponent(new Date(b).toISOString())}`;
}
/** Why a range can't be applied, in the words the picker shows; null when it can. */
export function rangeProblem(r, limits = {}) {
    if (!r.from || !r.to)
        return 'Pick a From and a To day.';
    if (r.from > r.to)
        return 'From must be on or before To.';
    if (limits.max && r.to > limits.max)
        return limits.max === todayIn() ? 'Pick days no later than today.' : `Pick days up to ${dayLabel(limits.max)}.`;
    if (limits.min && r.from < limits.min)
        return `Pick days from ${dayLabel(limits.min)} on.`;
    if (limits.maxDays && spanDays(r.from, r.to) > limits.maxDays)
        return `Pick at most ${limits.maxDays} days.`;
    if (r.from === r.to && r.fromTime && r.toTime && r.fromTime > r.toTime)
        return 'The From time must be before the To time.';
    return null;
}
/** The picker's quick ranges, all ending today or earlier. */
export const RANGE_SHORTCUTS = [
    { label: 'Today', range: (t) => ({ from: t, to: t }) },
    { label: 'Yesterday', range: (t) => ({ from: addDays(t, -1), to: addDays(t, -1) }) },
    { label: 'Last 7 days', range: (t) => ({ from: addDays(t, -6), to: t }) },
    { label: 'Last 30 days', range: (t) => ({ from: addDays(t, -29), to: t }) },
    { label: 'This month', range: (t) => ({ from: monthStart(t), to: t }) },
    { label: 'Last month', range: (t) => ({ from: addMonths(monthStart(t), -1), to: addDays(monthStart(t), -1) }) },
    { label: 'Last 3 months', range: (t) => ({ from: addDays(addMonths(t, -3), 1), to: t }) },
    { label: 'This year', range: (t) => ({ from: `${t.slice(0, 4)}-01-01`, to: t }) },
    { label: 'Last year', range: (t) => ({ from: `${+t.slice(0, 4) - 1}-01-01`, to: `${+t.slice(0, 4) - 1}-12-31` }) },
];
/**
 * Server side: reads optional `from` / `to` query values (ISO instants or `YYYY-MM-DD`, read as UTC
 * midnight; `to` exclusive). Both or neither; `from` before `to`; at most `maxDays` apart.
 */
export function parseRangeQuery(from, to, opts = {}) {
    const none = from === undefined || from === '';
    if (none && (to === undefined || to === ''))
        return { ok: true, from: null, to: null };
    const read = (v) => {
        if (typeof v !== 'string' || v.length > 40)
            return null;
        const ms = isDay(v) ? utcMs(v) : /^\d{4}-\d{2}-\d{2}T/.test(v) ? Date.parse(v) : NaN;
        return Number.isNaN(ms) ? null : new Date(ms);
    };
    const a = read(from);
    const b = read(to);
    if (!a || !b)
        return { ok: false, error: 'from and to must both be dates (ISO 8601).' };
    if (a >= b)
        return { ok: false, error: 'from must be before to.' };
    if (opts.maxDays && b.getTime() - a.getTime() > (opts.maxDays + 1) * DAY)
        return { ok: false, error: `The range can be at most ${opts.maxDays} days.` };
    return { ok: true, from: a, to: b };
}
//# sourceMappingURL=date-range.js.map